import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  Box,
  Chip,
  LinearProgress,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  TextField,
  IconButton,
} from '@mui/material';
import {
  Key,
  Apps,
  CheckCircle,
  Cancel,
  Schedule,
  DeviceHub,
  Warning,
  Edit,
  Save,
  Close,
  Person,
} from '@mui/icons-material';
import { License } from '../types/license';

interface LicenseDetailDialogProps {
  open: boolean;
  license: License | null;
  onClose: () => void;
  onUpdate?: (licenseId: string, updates: { expiresAt?: string }) => void;
}

export const LicenseDetailDialog: React.FC<LicenseDetailDialogProps> = ({
  open,
  license,
  onClose,
  onUpdate,
}) => {
  const [isEditingExpiry, setIsEditingExpiry] = useState(false);
  const [expiryDate, setExpiryDate] = useState('');

  // ダイアログが開かれたときに有効期限を初期化
  React.useEffect(() => {
    if (license?.expiresAt) {
      // ISO文字列をyyyy-MM-ddTHH:mm形式に変換
      const date = new Date(license.expiresAt);
      const localISOTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setExpiryDate(localISOTime);
    } else {
      setExpiryDate('');
    }
    setIsEditingExpiry(false);
  }, [license]);

  if (!license) return null;

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'default';
      case 'EXPIRED':
        return 'error';
      case 'REVOKED':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getActivationProgress = () => {
    const used = license.currentActivations || 0;
    const quantity = license.quantity || 1;
    const maxActivations = license.maxActivations || 1;
    // 実効的な最大同時起動数 = 購入数 × 同時起動数
    const effectiveMax = quantity * maxActivations;
    const percentage = (used / effectiveMax) * 100;

    return {
      percentage,
      effectiveMax,
      quantity,
      maxActivations,
      color: percentage >= 100 ? 'error' : percentage >= 80 ? 'warning' : 'primary'
    };
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '未設定';
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSaveExpiry = () => {
    if (onUpdate && expiryDate) {
      // ローカル時間をISO文字列に変換
      const isoDate = new Date(expiryDate).toISOString();
      onUpdate(license.id, { expiresAt: isoDate });
    }
    setIsEditingExpiry(false);
  };

  const handleCancelEdit = () => {
    // 元の値に戻す
    if (license?.expiresAt) {
      const date = new Date(license.expiresAt);
      const localISOTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      setExpiryDate(localISOTime);
    } else {
      setExpiryDate('');
    }
    setIsEditingExpiry(false);
  };

  const activationProgress = getActivationProgress();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <Key />
          ライセンス詳細
          <Chip
            label={license.status}
            color={getStatusColor(license.status)}
            size="small"
            sx={{ ml: 1 }}
          />
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          {/* 基本情報 */}
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                ライセンスキー
              </Typography>
              <Typography variant="body1" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                {license.key}
              </Typography>
            </Paper>
          </Grid>

          {/* アプリケーション情報 */}
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Apps color="primary" />
              <Typography variant="subtitle1">アプリケーション</Typography>
            </Box>
            <Typography variant="body2">
              {license.applicationName || license.applicationId || '未設定'}
            </Typography>
          </Grid>

          {/* ユーザー情報 */}
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Person color="primary" />
              <Typography variant="subtitle1">ユーザー名</Typography>
            </Box>
            <Typography variant="body2">
              {license.userName || '未設定'}
            </Typography>
          </Grid>

          {/* 有効期限 */}
          <Grid item xs={12} md={6}>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Schedule color="primary" />
              <Typography variant="subtitle1">有効期限</Typography>
              {!isEditingExpiry && (
                <IconButton
                  size="small"
                  onClick={() => setIsEditingExpiry(true)}
                  sx={{ ml: 1 }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              )}
            </Box>

            {isEditingExpiry ? (
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  type="datetime-local"
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <IconButton size="small" onClick={handleSaveExpiry} color="primary">
                  <Save fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={handleCancelEdit}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Typography variant="body2">
                {license.expiresAt ? (
                  <>
                    {formatDate(license.expiresAt)}
                    {new Date(license.expiresAt) < new Date() && (
                      <Chip
                        label="期限切れ"
                        color="error"
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    )}
                  </>
                ) : (
                  '無期限'
                )}
              </Typography>
            )}
          </Grid>

          {/* アクティベーション状況 */}
          <Grid item xs={12}>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <DeviceHub color="primary" />
              <Typography variant="subtitle1">アクティベーション状況</Typography>
            </Box>

            {/* 購入数表示（quantity > 1の場合のみ） */}
            {activationProgress.quantity > 1 && (
              <Box mb={2} p={1.5} bgcolor="info.light" borderRadius={1}>
                <Typography variant="body2" gutterBottom>
                  <strong>サブスクライト購入数:</strong> {activationProgress.quantity}
                </Typography>
                <Typography variant="body2">
                  <strong>実効同時起動数:</strong> {activationProgress.effectiveMax}台
                  （購入数 {activationProgress.quantity} × 同時起動数 {activationProgress.maxActivations}）
                </Typography>
              </Box>
            )}

            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">
                  使用中: {license.currentActivations || 0} / {activationProgress.effectiveMax}
                  {activationProgress.quantity > 1 && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                      (基本: {activationProgress.maxActivations}台)
                    </Typography>
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {Math.round(activationProgress.percentage)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={activationProgress.percentage}
                color={activationProgress.color as any}
                sx={{ height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                残り {Math.max(0, activationProgress.effectiveMax - (license.currentActivations || 0))} 台のデバイスをアクティベート可能
              </Typography>
            </Box>

            {(license.currentActivations || 0) >= activationProgress.effectiveMax && (
              <Box display="flex" alignItems="center" gap={1} p={1.5} bgcolor="warning.light" borderRadius={1}>
                <Warning color="warning" />
                <Typography variant="body2">
                  アクティベーション上限に達しています。
                  新しいデバイスをアクティベートするには、既存のデバイスを非アクティベートしてください。
                </Typography>
              </Box>
            )}
          </Grid>

          {/* メタデータ */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary">詳細情報</Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary="作成日時"
                  secondary={formatDate(license.issuedAt)}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="更新日時"
                  secondary={formatDate(license.updatedAt)}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary="ライセンスID"
                  secondary={license.id}
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
};